import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';

import {
  BackgroundColor,
  CardsCarousel,
  CardsCarouselCard,
  CmsUrl,
  CtaButton,
  InlineImage,
} from '@digital-platform/rs-cms/domain';
import { ViewportService } from '@digital-platform/rs-shared/services';
import { IconComponent } from '@digital-platform/rs-shared/ui-brand';
import { BasePTypeComponent } from '../base-ptype/base-ptype.component';
import { CardsCarouselCardComponent } from '../cards-carousel-card/cards-carousel-card.component';
import { CtaButtonComponent } from '../cta-button/cta-button.component';
import { InlineImageComponent } from '../inline-image/inline-image.component';
import { RichTextComponent } from '../rich-text/rich-text.component';

@Component({
  selector: 'digital-platform-cards-carousel',
  standalone: true,
  imports: [
    CommonModule,
    CardsCarouselCardComponent,
    RichTextComponent,
    InlineImageComponent,
    CtaButtonComponent,
    IconComponent,
  ],
  templateUrl: './cards-carousel.component.html',
  styleUrls: ['./cards-carousel.component.scss'],
  animations: [
    trigger('accordionOpenClose', [
      transition(':enter', [
        style({ opacity: 0, height: 0 }),
        animate(
          '0.25s ease-out',
          style({ marginBottom: '-10px', opacity: 1, height: '*' })
        ),
      ]),
      transition(':leave', [
        animate('0.20s', style({ opacity: 0, height: 0 })),
      ]),
    ]),
  ],
})
export class CardsCarouselComponent
  extends BasePTypeComponent
  implements CardsCarousel, OnChanges, AfterViewInit
{
  public viewportService = inject(ViewportService);
  public router = inject(Router);

  public override type = 'cardsCarousel' as const;

  @ViewChild('carouselContent', { read: ElementRef, static: true })
  public carouselContent!: ElementRef<Element>;

  @Input() public bgColor: BackgroundColor | null = {};
  @Input() public cardsCarouselCard: CardsCarouselCard[] | null = [];

  @Input() public ctaButton: CtaButton[] | null = [];

  @Input() public richTextTitle: string | null = '';
  @Input() public inlineImage: InlineImage[] | null = [];
  @Input() public showAccordion: boolean | null = false;

  showCards = true;
  scrollAmount = 410;
  imageWidthDesktop = 400;
  backgroundColor = '';
  isScrollable = false;

  ngAfterViewInit(): void {
    // Prevent throwing the error `NG0100: Expression has changed after it was checked`
    // Can only do this after the view has been initialized as we need the scrollWidth and clientWidth
    setTimeout(() => {
      this.checkIfIsScrollable();
    }, 0);
  }

  ngOnChanges(): void {
    this.imageWidthDesktop = this.getHeight();
    this.scrollAmount = this.imageWidthDesktop + 10;
    /* 10 is the gap amount between images, helps it look clean */
    this.backgroundColor = this.bgColor?.cssColorName ?? '';

    if (this.carouselContent?.nativeElement) {
      this.checkIfIsScrollable();
    }
  }

  scrollLeft() {
    this.carouselContent.nativeElement.scrollTo({
      left: this.carouselContent.nativeElement.scrollLeft - this.scrollAmount,
      behavior: 'smooth',
    });
    setTimeout(this.canScrollLeft, 700); // kick change detection
  }

  getHeight(): number {
    if (this.cardsCarouselCard?.[0]?.inlineImage?.[0]?.imageWidth) {
      return Number(this.cardsCarouselCard?.[0]?.inlineImage?.[0]?.imageWidth);
    } else if (
      this.cardsCarouselCard?.[0]?.inlineImage?.[0]?.webpMedia?.width
    ) {
      return Number(
        this.cardsCarouselCard?.[0]?.inlineImage?.[0]?.webpMedia?.width
      );
    }
    return 400;
  }

  scrollRight() {
    this.carouselContent.nativeElement.scrollTo({
      left: this.carouselContent.nativeElement.scrollLeft + this.scrollAmount,
      behavior: 'smooth',
    });
    setTimeout(this.canScrollRight, 700); // kick change detection
  }

  canScrollRight(): boolean {
    if (this.carouselContent) {
      const range =
        this.carouselContent.nativeElement.scrollWidth -
        this.carouselContent.nativeElement.clientWidth -
        this.carouselContent.nativeElement.scrollLeft;
      return !((range <= 150 && range >= 0) || range < 0);
    } else {
      return false;
    }
  }

  canScrollLeft(): boolean {
    if (this.carouselContent) {
      return !(this.carouselContent.nativeElement.scrollLeft === 0);
    } else {
      return false;
    }
  }

  @HostListener('window:resize', ['$event'])
  checkIfIsScrollable(): void {
    // Prevent throwing the error `NG0100: Expression has changed after it was checked`
    if (!this.carouselContent) {
      this.isScrollable = false;
    } else {
      this.isScrollable =
        this.carouselContent.nativeElement.scrollWidth >
        this.carouselContent.nativeElement.clientWidth;
    }
  }

  onScroll() {
    // kick change detection
  }

  navigate(url: CmsUrl | null, e: Event): void {
    e.stopPropagation();

    if (!url || !url[0]) {
      return;
    }
    this.router.navigate([`${url[0].uri}`]);
  }
}
